/**
 * GuestServicesActivitiesModal — opens a guest's activities & services in a
 * dialog. Fed by the shared person surfaces (the global NFC popover, the
 * person search). The guest is resolved from a personId (a scan/search yields a
 * Person, not a Guest), then the reusable GuestServicesActivitiesPanel renders.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/theme/components/ui/dialog';
import { LoadingState } from '@/shared/components';
import { getGuestByPersonId } from '@/modules/guests';
import { GuestServicesActivitiesPanel } from './GuestServicesActivitiesPanel';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personId?: string;
  personName?: string;
}

export function GuestServicesActivitiesModal({ open, onOpenChange, personId, personName }: Props) {
  const { t } = useTranslation();

  const { data: guest, isLoading } = useQuery({
    queryKey: ['guest-by-person', personId],
    queryFn: () => getGuestByPersonId(personId as string),
    enabled: open && !!personId,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {personName
              ? t('services.guestPanel.modalTitle', '{{name}} · activities & services', { name: personName })
              : t('services.guestPanel.title', 'Activities & services')}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <LoadingState />
        ) : guest?.id ? (
          <GuestServicesActivitiesPanel
            guestId={guest.id}
            bookingId={guest.bookingId}
            checkIn={guest.checkInDate}
            checkOut={guest.checkOutDate}
            guestName={personName}
          />
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('services.guestPanel.notAGuest', 'This person is not a guest at any location.')}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
